import { faCamera, faCancel, faRotateRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { delay, fullAddress, Geocoder, hasValue, LocationTracker, log, styles } from "@repo/helpers";
import { address, SerializedFile } from "@repo/models";
import { Button, ButtonSize, ButtonTypes, InputComponent, InputComponentProps, Loading } from "@repo/ui";
import DomToImage from "dom-to-image";
import GoogleMapReact from "google-map-react";
import { useEffect, useRef, useState } from "react";
import { isMobile } from "react-device-detect";

import GoogleMap from "../GoogleMap";
import { MapMarker } from "../GoogleMap/types";
import style from "./style.module.scss";

const _elementsToIgnore = ["customMapControlButtonsDiv", "gmnoprint", "gm-style-cc"];
const _addressToCaptureMarkerName = "addressToCapture";

const _domtoimageOptions = {
	filter: (node: any) => {
		const invalidElement = node.currentSrc && node.currentSrc.includes("data:image/svg+xml");
		const ignoreElement =
			typeof node.className === "string" &&
			_elementsToIgnore.some((elementToIgnore) => node.className.indexOf(elementToIgnore) > -1);
		return !(invalidElement || ignoreElement);
	},
} as DomToImage.Options;

const _googleMapOptions = {
	zoomControl: false,
	fullscreenControl: false,
	mapTypeControl: false,
	streetViewControl: false,
	keyboardShortcuts: false,
	gestureHandling: "greedy",
	mapTypeId: "hybrid",
} as GoogleMapReact.MapOptions;

export default function GoogleMapsCapture(props: Props) {
	const { id, value, addressToCapture, locationTracker, changeHandler } = props;

	const [mapData, setMapData] = useState(value?.url);
	const [mapRef, setMapRef] = useState<HTMLElement>(null);
	const [capturing, setCapturing] = useState(false);
	const [showMap, setShowMap] = useState(() => !hasValue(mapData));
	const [showImage, setShowImage] = useState(() => hasValue(mapData));
	const [showCancelButton, setShowCancelButton] = useState(false);
	const [coordsToCapture, setCoordsToCapture] = useState<GoogleMapReact.Coords>(null);
	const [center, setCenter] = useState<GoogleMapReact.Coords>(null);
	const [error, setError] = useState<string>(null);
	const [mapReady, setMapReady] = useState(false);

	const accuracyCircle = useRef<any>(null);
	const addressStr = addressToCapture && JSON.stringify(addressToCapture);
	useEffect(() => {
		if (hasValue(addressToCapture)) convertToCoords(addressToCapture);
		else setMapReady(true);
	}, [addressStr]);

	useEffect(() => {
		setCenter(coordsToCapture);
	}, [coordsToCapture]);

	async function convertToCoords(address: address) {
		const formattedAddress = fullAddress(
			address.address1,
			address.address2,
			address.city,
			address.stateName,
			address.zip,
		);
		try {
			const result = await new Geocoder().fromAddress(formattedAddress);

			const { types, geometry } = result.results[0];
			const validAddress =
				(types as string[]).includes("premise") || (types as string[]).includes("street_address");
			if (validAddress)
				setCoordsToCapture({
					lat: geometry.location.lat,
					lng: geometry.location.lng,
				});
			else {
				setError(`Could not geolocate the address "${formattedAddress}" on the map.`);
				log(`Could not geolocate the address (${formattedAddress}).`, "error");
			}
		} catch (ex) {
			setError(`Invalid Address: ${formattedAddress}`);
			log(`Could not geolocate the address (${formattedAddress}). Geocode error: ${ex}`, "error");
		} finally {
			setMapReady(true);
		}
	}

	async function captureButtonOnClick() {
		if (accuracyCircle.current) accuracyCircle.current.setMap(null);
		await delay(100);
		setCapturing(true);
		const image = await DomToImage.toPng(mapRef, {
			..._domtoimageOptions,
			height: mapRef.clientHeight,
			width: mapRef.clientWidth,
		});
		setMapData(image);
		setCapturing(false);
		setShowMap(false);
		setShowImage(true);
		setShowCancelButton(false);
		const file = {
			name: props.label,
			type: "image/png",
			url: image,
		} as SerializedFile;
		changeHandler(file, id);
	}

	function retakeButtonOnClick() {
		setShowMap(true);
		setShowImage(false);
		setShowCancelButton(true);
	}

	function cancelButtonOnClick() {
		setShowMap(false);
		setShowImage(true);
		setShowCancelButton(false);
	}

	return (
		<InputComponent {...props} hideInputGroupClass hideAddon>
			{renderGoogleMap()}
			{renderMapImage()}
			{renderError()}
			{renderButtons()}
		</InputComponent>
	);

	function renderGoogleMap() {
		const mobileStyle = isMobile && style.mobileMapContainer;
		if (!showMap || !mapReady) return null;
		const markers =
			hasValue(coordsToCapture) && hasValue(center)
				? ([
						{
							name: _addressToCaptureMarkerName,
							lat: center.lat,
							lng: center.lng,
						},
					] as MapMarker[])
				: [];
		const markersToCenterOn = hasValue(coordsToCapture) ? [_addressToCaptureMarkerName] : [];
		const centerOnCurrentLocation = hasValue(coordsToCapture) ? false : true;

		return (
			<>
				<h5 className={style.header}>Live Map</h5>
				<div className={styles(style.mapContainer, mobileStyle)}>
					<GoogleMap
						options={_googleMapOptions}
						markers={markers}
						center={center}
						locationTracker={locationTracker}
						markersToCenterOn={markersToCenterOn}
						centerOnCurrentLocation={centerOnCurrentLocation}
						accuracyCircleRef={accuracyCircle}
						setMapRef={setMapRef}
						showCurrentLocationMarker
					/>
				</div>
			</>
		);
	}

	function renderMapImage() {
		if (!showImage) return null;
		return (
			<>
				<h5 className={style.header}>Image</h5>
				<div className={style.imageContainer}>
					<img src={mapData} alt="map" />
				</div>
			</>
		);
	}

	function renderButtons() {
		const cancelButton = (
			<Button
				onClick={cancelButtonOnClick}
				types={[ButtonTypes.Danger]}
				size={ButtonSize.large}
				className={style.cancelButton}
			>
				<FontAwesomeIcon icon={faCancel} color={"black"} size={"3x"} />
			</Button>
		);

		const captureButtonOnClickFn = showMap ? captureButtonOnClick : retakeButtonOnClick;
		const captureButtonIcon = (
			<FontAwesomeIcon icon={showMap ? faCamera : faRotateRight} color={"black"} size={"3x"} />
		);
		return (
			<Loading message="Capturing..." loading={capturing}>
				<div className={style.buttonContainer}>
					{showCancelButton && cancelButton}
					<Button onClick={captureButtonOnClickFn} types={[ButtonTypes.Primary]} size={ButtonSize.large}>
						{captureButtonIcon}
					</Button>
				</div>
			</Loading>
		);
	}

	function renderError() {
		if (!hasValue(error)) return;
		return <h6 className={style.error}>{error}</h6>;
	}
}

interface Props extends InputComponentProps<any, SerializedFile> {
	value?: SerializedFile;

	addressToCapture?: address;
	locationTracker?: LocationTracker;
}
